export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export const VISITOR_KEY = 'osvi.chat.visitor';
const VISITOR_FORMAT = /^web:[0-9a-f]{32}$/;

export function memoryStore(): KeyValueStore {
  const values = new Map<string, string>();
  return {
    get: (key) => values.get(key) ?? null,
    set: (key, value) => void values.set(key, value),
    remove: (key) => void values.delete(key),
  };
}

export function browserStore(): KeyValueStore {
  const memory = memoryStore();
  let broken = false;
  const attempt = <T>(operation: () => T, fallback: () => T): T => {
    if (broken) return fallback();
    try {
      return operation();
    } catch {
      broken = true;
      return fallback();
    }
  };

  return {
    get: (key) => attempt(() => window.localStorage.getItem(key), () => memory.get(key)),
    set: (key, value) => attempt(() => window.localStorage.setItem(key, value), () => memory.set(key, value)),
    remove: (key) => attempt(() => window.localStorage.removeItem(key), () => memory.remove(key)),
  };
}

const toVisitorId = (bytes: Uint8Array): string =>
  `web:${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;

let ephemeralId: string | null = null;

export function getVisitorId(store: KeyValueStore, random?: () => Uint8Array): string {
  const existing = store.get(VISITOR_KEY);
  if (existing && VISITOR_FORMAT.test(existing)) return existing;
  if (existing !== null) store.remove(VISITOR_KEY);

  let bytes: Uint8Array | undefined;
  if (random) {
    bytes = random();
  } else {
    try {
      bytes = globalThis.crypto?.getRandomValues?.(new Uint8Array(16));
    } catch {
      // Some browsers expose crypto but deny access to its random source.
    }
  }

  if (bytes) {
    const id = toVisitorId(bytes);
    store.set(VISITOR_KEY, id);
    return id;
  }

  if (!ephemeralId) {
    const weak = new Uint8Array(16);
    for (let index = 0; index < weak.length; index += 1) {
      weak[index] = Math.floor(Math.random() * 256);
    }
    ephemeralId = toVisitorId(weak);
  }

  return ephemeralId;
}

export function sessionHintKey(agentId: string): string {
  return `osvi.chat.session.${agentId}`;
}
